import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('dialog:open', this.#handleDialogOpen);
    this.addEventListener('dialog:close', this.#handleDialogClose);
    this.refs.dialog?.addEventListener('close', this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('dialog:open', this.#handleDialogOpen);
    this.removeEventListener('dialog:close', this.#handleDialogClose);
    this.refs.dialog?.removeEventListener('close', this.#handleDialogClose);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #handleDialogOpen = () => {
    const root = document.documentElement;
    const bodyTop = document.body?.style?.top || '';
    const parsedTop = Number.parseInt(bodyTop, 10);
    const scrollOffset = Number.isFinite(parsedTop)
      ? Math.abs(parsedTop)
      : window.scrollY || 0;
    root.style.setProperty('--cart-drawer-scroll-offset', `${scrollOffset}px`);
    root.setAttribute('data-cart-drawer-open', '');
  };

  #handleDialogClose = () => {
    const root = document.documentElement;
    root.removeAttribute('data-cart-drawer-open');
    root.style.removeProperty('--cart-drawer-scroll-offset');
    this.#forceScrollUnlock();
    this.#syncHeaderAfterClose();
  };

  #forceScrollUnlock() {
    const body = document.body;
    const bodyTop = body?.style?.top || '';
    const parsedTop = Number.parseInt(bodyTop, 10);

    body.style.width = '';
    body.style.position = '';
    body.style.top = '';

    if (Number.isFinite(parsedTop)) {
      window.scrollTo({ top: Math.abs(parsedTop), behavior: 'auto' });
    }

    document.documentElement.removeAttribute('scroll-lock');
  }

  #syncHeaderAfterClose = () => {
    const header = document.querySelector('#header-component');
    if (!(header instanceof HTMLElement)) return;
    if (header.getAttribute('sticky') !== 'scroll-up') return;

    const scrollingElement =
      document.scrollingElement ?? document.documentElement;
    const scrollTop = scrollingElement?.scrollTop ?? window.scrollY ?? 0;
    const rect = header.getBoundingClientRect();
    const headerIsInView = rect.top >= 0;

    if (scrollTop <= 0 || headerIsInView) {
      header.dataset.stickyState = 'inactive';
      header.dataset.scrollDirection = 'none';
      header.removeAttribute('data-animating');
      return;
    }

    document.dispatchEvent(new Event('scroll'));
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
